"use client";

import { fireConfetti } from "@shared/lib/confetti";
import { useEffect } from "react";

/**
 * Celebrates the browser returning from checkout. Payment counts come from the
 * server-side `payment-received` event; the return itself is not reported.
 *
 * There is no dedicated success page — checkout's success_url is whatever
 * page the user was on. Creem appended `checkout_id` (+ order/product ids)
 * on its own; Waffo does not document what it appends, so we build the
 * success_url ourselves in the Waffo provider and put `checkout_ref` on it.
 * Either param marks a checkout return, not a verified payment. Mounted in
 * Document so every possible return page is covered.
 */
export function PurchaseCompletedTracker() {
	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		// checkout_ref 是 Waffo 通路里我们自己拼上去的；checkout_id 是 Creem 留下
		// 的，存量订阅续费回跳仍会带，两个都认。
		const checkoutId =
			params.get("checkout_ref") ?? params.get("checkout_id");
		if (!checkoutId) {
			return;
		}

		// The params survive reloads and back-navigation; remember the
		// checkout so it's only celebrated once. The key keeps its old
		// purchase-completed prefix so returns already seen don't fire again.
		try {
			const dedupeKey = `purchase-completed:${checkoutId}`;
			if (localStorage.getItem(dedupeKey)) {
				return;
			}
			localStorage.setItem(dedupeKey, "1");
		} catch {
			// localStorage unavailable — still celebrate; worst case a reload
			// fires it twice.
		}

		// There is no success page to celebrate on — checkout returns the user
		// to whatever page they left from, with no visible confirmation that
		// the money went through. The burst is that confirmation.
		void fireConfetti({
			particleCount: 160,
			spread: 90,
			origin: { y: 0.6 },
		});
	}, []);

	return null;
}
